import { FC, createContext, useEffect, useMemo, useReducer } from "react";

import { AuthProviderProps, AuthStateActions, AuthStateProps, ReportListingProps, UserContextProps } from "./types";
import { HCPUserSubHeaderProps, ManufacturerSubHeaderProps, UserType } from "../../types/users";
import { authContextReducer, initialState } from "./reducer";
import { shutDownAndEndChat } from "src/helpers/tawkto";
import { USER_TYPES } from "src/constants";

export const AuthContext = createContext<UserContextProps>({
  isUserAuthorized: false,
  isManufacturer: false,
  authToken: '',
  userData: null,
  isRestoreCalled: false,
  sub_header: null,
  report_listing: {
    "Field Liaison Report": false,
    "Full Report": false,
    "Incoming Fax Report": false,
    "Login Report": false,
    "Status Report": false,
    "Refill Report": false,
    // "Pre Condensed Sales Report": false,
    // "Pre Refill Report": false,
    // "New Full Report": false,
    // "Old Full Report": false,
    "Condensed Sales Report": false,

  },
  createSession: (userData: UserType, report_listing: ReportListingProps, sub_header: HCPUserSubHeaderProps | ManufacturerSubHeaderProps , authToken: string) => {},
  destroySession: () => {},
  restoreSession: () => false,
  updateUserToken: (userToken: string) => {},
  updateUserData: (userData: UserType) => {},
});

const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authContextReducer, initialState);
  const authChannel = useMemo(() => new BroadcastChannel("urogen_auth"), []);

  const createSession = (userData: UserType, report_listing: ReportListingProps, sub_header: HCPUserSubHeaderProps | ManufacturerSubHeaderProps, authToken: string) => {
    const data: AuthStateProps = {
      authToken,
      userData,
      sub_header,
      report_listing,
      isUserAuthorized: true,
      isRestoreCalled: false,
      isManufacturer: USER_TYPES.MANUFACTURER.includes(userData.ref_type)
    };
    dispatch({
      type: AuthStateActions.SET_SESSION,
      payload: data,
    });

    localStorage.setItem('token', authToken);
    localStorage.setItem('data', JSON.stringify({userData, sub_header, report_listing}));
    authChannel.postMessage(userData);
  };

  const destroySession = () => {
    //emptyCacheAndCookies()
    localStorage.clear();
    authChannel.postMessage(null);
    dispatch({ type: AuthStateActions.CLEAR_SESSION });
  };

  const updateUserData = (userData: UserType) => {
    dispatch({
      type: AuthStateActions.UPDATE_USER_DATA,
      payload: userData,
    });
    localStorage.setItem('data', JSON.stringify({ userData, sub_header: state.sub_header, report_listing: state.report_listing }));
  };

  const updateUserToken = (userToken: string) => {
    dispatch({
      type: AuthStateActions.UPDATE_USER_TOKEN,
      payload: userToken
    });
    localStorage.removeItem("token")
    localStorage.setItem("token", userToken);
  };

  const restoreSession = (): boolean => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('data');
    const data = {} as AuthStateProps;

    if (token && userData) {
      data.isUserAuthorized = true;
      data.authToken = token;
      const dataInLC = JSON.parse(userData);
      data.userData = dataInLC.userData;
      data.sub_header = dataInLC.sub_header;
      data.report_listing = dataInLC.report_listing;
      data.isManufacturer = USER_TYPES.MANUFACTURER.includes(dataInLC.userData.ref_type);
      data.isRestoreCalled = true;
      dispatch({
        type: AuthStateActions.SET_SESSION,
        payload: data,
      });
      return true;
    } else {
      dispatch({
        type: AuthStateActions.SET_SESSION,
        payload: {
          ...initialState,
          isRestoreCalled: true,
        },
      });
      return false;
    }
  };

  useEffect(() => {
    authChannel.onmessage = (_isAuthorized) => {
      if (_isAuthorized.data === null) {
        localStorage.clear();
        dispatch({ type: AuthStateActions.CLEAR_SESSION });
        shutDownAndEndChat();
      }
      else {
        restoreSession();
      }
    }
  }, [authChannel]);

  authChannel.onmessage = (_isAuthorized) => {
    if (_isAuthorized.data === null) {
      localStorage.clear();
      dispatch({ type: AuthStateActions.CLEAR_SESSION });
      shutDownAndEndChat();
    }
    else {
      restoreSession();
    }
  }

  return (
    <AuthContext.Provider value={{
      isUserAuthorized: state.isUserAuthorized,
      isManufacturer: state.isManufacturer,
      authToken: state.authToken,
      userData: state.userData,
      isRestoreCalled: state.isRestoreCalled,
      sub_header: state.sub_header,
      report_listing: state.report_listing,
      createSession,
      destroySession,
      restoreSession,
      updateUserData,
      updateUserToken,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
